import { useState, type ReactNode } from 'react'
import { referralReceivingNotice } from '@entities/referrals/referrals'
import { FileQuestion, Users } from 'lucide-react'
import {
  Banner,
  Button,
  Card,
  EmptyState,
  ErrorState,
  MoneyValue,
  Skeleton,
  StatusBadge,
  TextField,
} from '@shared/design-system/ui'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { toast } from '@shared/lib/toast/toast-store'
import { useReferralAccess, useReferrals } from '../api/referrals'
import * as styles from './referrals.css'

export function ReferralsScreen({ receivingNotice }: { receivingNotice?: ReactNode }) {
  const enabled = useFeatureFlag('referralProgram')
  const [offset, setOffset] = useState(0)
  const access = useReferralAccess()
  const query = useReferrals(offset, enabled && access.data?.enabled === true && !access.isError)
  if (!enabled || access.data?.enabled === false || query.data?.enabled === false)
    return (
      <EmptyState
        data-testid="referrals-unavailable"
        icon={<FileQuestion />}
        title="Página indisponível"
        description="Esta página não está disponível."
      />
    )
  if (access.isError || query.isError)
    return (
      <ErrorState
        title="Não foi possível carregar esta página"
        onRetry={() => void (access.isError ? access.refetch() : query.refetch())}
      />
    )
  if (access.isPending || query.isPending)
    return (
      <div data-testid="referrals-loading">
        <Skeleton height={320} />
      </div>
    )
  const data = query.data
  const copy = async () => {
    if (!data.inviteUrl) return
    try {
      await navigator.clipboard.writeText(data.inviteUrl)
      toast.success('Link de indicação copiado.')
    } catch {
      toast.error('Não foi possível copiar. Selecione o link e copie manualmente.')
    }
  }
  return (
    <section className={styles.stack} data-testid="referrals-screen">
      <header className={styles.header}>
        <div className={styles.metric}>
          <h1 className={styles.title}>Indique e ganhe</h1>
          <p className={styles.copy}>
            Apresente a VendePay a novos vendedores e acompanhe suas comissões.
          </p>
        </div>
        <StatusBadge tone="success" label="Habilitado" />
      </header>
      <Banner
        tone="info"
        title={`${Number(data.percentage).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}% sobre as vendas dos seus indicados`}
      >
        A comissão sai da taxa VendePay, limitada ao líquido disponível após os custos do pagamento.
        Reembolsos e chargebacks estornam a comissão proporcionalmente. O repasse segue as condições
        da sua conta de recebimento. A taxa atual vale para novos pagamentos; comissões anteriores
        mantêm a taxa registrada.
      </Banner>
      <p className={styles.copy} data-testid="referrals-receiving-guidance">
        {referralReceivingNotice}
      </p>
      {receivingNotice}
      {data.inviteUrl ? (
        <Card className={styles.stack} data-testid="referrals-invite">
          <h2 className={styles.heading}>Seu link de indicação</h2>
          <p className={styles.copy}>
            Compartilhe este link. O vínculo é criado quando o novo vendedor conclui o cadastro.
          </p>
          <div className={styles.invite}>
            <TextField
              label="Link de indicação"
              value={data.inviteUrl}
              readOnly
              data-testid="referrals-link"
            />
            <Button data-testid="referrals-copy-btn" onClick={() => void copy()}>
              Copiar link
            </Button>
          </div>
        </Card>
      ) : null}
      <div className={styles.stats} data-testid="referrals-totals">
        <Card className={styles.metric}>
          <span className={styles.copy}>Vendedores indicados</span>
          <strong className={styles.title}>{data.total}</strong>
        </Card>
        <Card className={styles.metric}>
          <span className={styles.copy}>Comissões líquidas</span>
          <MoneyValue amount={data.totals.net} currency="BRL" size="xl" />
        </Card>
        <Card className={styles.metric}>
          <span className={styles.copy}>Aguardando repasse</span>
          <MoneyValue amount={data.totals.pending} currency="BRL" size="xl" />
        </Card>
      </div>
      <Card data-testid="referrals-list">
        <div className={styles.header}>
          <h2 className={styles.heading}>Suas indicações</h2>
          <Button
            variant="secondary"
            data-testid="referrals-refresh-btn"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Atualizar
          </Button>
        </div>
        <p className={styles.copy}>
          O vínculo é confirmado ao concluir o cadastro pelo seu link. Isso não confirma aprovação
          cadastral nem realização de vendas. Os ganhos aparecem conforme as comissões são geradas.
        </p>
        {data.items.length ? (
          data.items.map((item) => (
            <article key={item.id} className={styles.row} data-testid={`referrals-card-${item.id}`}>
              <div className={styles.person}>
                <strong>{item.name}</strong>
                <span className={styles.copy}>
                  Desde {new Date(item.joinedAt).toLocaleDateString('pt-BR')}
                </span>
              </div>
              <div className={styles.person}>
                <span className={styles.copy}>Comissão líquida</span>
                <MoneyValue amount={item.earnings.net} currency="BRL" />
              </div>
              <div className={styles.person}>
                <span className={styles.copy}>Estornada</span>
                <MoneyValue amount={item.earnings.reversed} currency="BRL" />
              </div>
            </article>
          ))
        ) : (
          <EmptyState
            icon={<Users />}
            title="Nenhuma indicação ainda"
            description="Os vendedores que se cadastrarem pelo seu link aparecerão aqui."
          />
        )}
        {offset > 0 || data.nextOffset !== null ? (
          <div className={styles.header}>
            <Button
              variant="secondary"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 20))}
              data-testid="referrals-previous-btn"
            >
              Anterior
            </Button>
            <Button
              variant="secondary"
              disabled={data.nextOffset === null}
              onClick={() => {
                if (data.nextOffset !== null) setOffset(data.nextOffset)
              }}
              data-testid="referrals-next-btn"
            >
              Próxima
            </Button>
          </div>
        ) : null}
        <p className={styles.copy}>
          Comissões líquidas já descontam estornos. Repasses podem estar em processamento e não
          representam saldo bancário disponível.
        </p>
      </Card>
    </section>
  )
}
